import { TranslateFn } from 'hooks/useTranslate';
import { DetailsProfileBadge } from 'modules/_shared/components/Sections/DetailsProfileSection';
import { getPartnerSindicatedTooltip } from 'modules/partners/utils/getPartnerSindicatedTooltip';
import { parseBadgesByCorporateId } from 'modules/partners/utils/parseBadgesByCorporateId';
import { Partner, PartnerCategory, PartnerRepresentant } from 'types';
import { SocietyCorporateId } from 'types/Society';
import activePartner from 'utils/activePartner';

type GetPartnerProfileBadgesProps = {
  corporateId: SocietyCorporateId;
  partner: Partner;
  categories: PartnerCategory[];
  partnerRepresentants: PartnerRepresentant[];
  isAdmin: boolean;
  t: TranslateFn;
};

const DEFAULT_CATEGORY_CO_OWNERSHIP = 'DEFAULT_CATEGORY_CO_OWNERSHIP';

const defaultCategoryStyle = 'info';

const getCategoryClassName = (style: string): string => {
  const currentStyle = style || defaultCategoryStyle;
  return `badge-outline-${currentStyle} text-${currentStyle}`;
};

const checkHasCoOwnerCategory = (categories: PartnerCategory[]): boolean =>
  categories.some(
    (category) => category.name === DEFAULT_CATEGORY_CO_OWNERSHIP
  );

const getActualPartnerBadge = (
  isActivePartner: boolean,
  isNewPartner: boolean,
  hasCoOwnerCategory: boolean,
  t: TranslateFn
) => {
  if (hasCoOwnerCategory) {
    return {
      text: t('Partner'),
      className: '',
    };
  }
  if (isNewPartner) {
    return null;
  }
  return {
    text: isActivePartner ? t('Partner') : t('ExPartner'),
    className: isActivePartner ? '' : 'badge-outline-danger text-danger',
  };
};

const getCategoryBadges = (
  categories: PartnerCategory[],
  isAdmin: boolean,
  t: TranslateFn
) =>
  categories?.length && isAdmin
    ? categories.map((category) => ({
        text: category?.isDefault ? t(category.name) : category?.name,
        className: getCategoryClassName(category.style),
        tooltip: category?.isDefault
          ? t(category?.description || '')
          : category?.description || '',
      }))
    : [];

const getSindicationBadge = (
  partner: Partner,
  partnerRepresentants: PartnerRepresentant[],
  t: TranslateFn
) =>
  partner?.sindication
    ? {
        text: t('Sindicated'),
        className: 'text-primary cursor-help',
        tooltip: getPartnerSindicatedTooltip({
          partner,
          partnerRepresentants,
          t,
        }),
      }
    : null;

export const getPartnerProfileBadges = ({
  corporateId,
  partner,
  categories,
  partnerRepresentants,
  isAdmin,
  t,
}: GetPartnerProfileBadgesProps): (DetailsProfileBadge | null)[] => {
  const isActivePartner = activePartner(partner);

  const isNewPartner = !partner?.incorporationDate;

  const hasCoOwnerCategory = checkHasCoOwnerCategory(categories);
  const partnerBadge = getActualPartnerBadge(
    isActivePartner,
    isNewPartner,
    hasCoOwnerCategory,
    t
  );

  const categoryBadges = getCategoryBadges(categories, isAdmin, t) || [];

  const sindicationBadge = getSindicationBadge(
    partner,
    partnerRepresentants,
    t
  );

  const badgesList = parseBadgesByCorporateId({
    corporateId,
    categories,
    partnerBadge,
    categoryBadges,
    sindicationBadge,
  });

  return badgesList;
};
